/**
 * Agentic Analytics — shared response contract + design tokens.
 *
 * Single source of truth that the Agentic Analyst API targets and the frontend
 * consumes. Mirrors `agentic-analytics-api-contract.md`:
 *   1 universal card · 4 block types · 17 chart types · 4 UI states.
 */

export type AnalystState = 'success' | 'loading' | 'empty' | 'error';

/** The 4 supported block types (contract §4). */
export type AnalystBlockType = 'chart' | 'metric' | 'grid' | 'markdown';

/** The 17 supported chart types (contract §9). */
export type AnalystChartType =
  // SERIES (cartesian)
  | 'bar'
  | 'hbar'
  | 'line'
  | 'step'
  | 'multi_bar'
  | 'stacked_bar'
  | 'multi_line'
  | 'combo'
  | 'pareto'
  | 'waterfall'
  | 'bullet'
  // CATEGORICAL (name / value)
  | 'donut'
  | 'funnel'
  | 'gauge'
  // HIERARCHY (tree nodes)
  | 'treemap'
  | 'decomp_tree'
  // XY (points / matrix)
  | 'heatmap'
  | 'scatter';

/** Value/number formats. `tiles[].format` uses the first four (contract §9). */
export type AnalystValueFormat =
  | 'number'
  | 'number-compact'
  | 'percent'
  | 'currency'
  | 'days';

/** SERIES value series (contract §5 — `config.yFields[]`). */
export interface AnalystSeries {
  field: string;
  label?: string;
  color?: string;
  type?: 'bar' | 'line';
  axis?: 'left' | 'right';
  stack?: string;
}

/** Period-over-period change chip (contract §6 — `tiles[].delta`). */
export interface AnalystDelta {
  value: number;
  direction?: 'up' | 'down';
  label?: string;
}

/** Mini trend line beside a metric value (contract §6 — `tiles[].sparkline`). */
export interface AnalystSparkline {
  points?: number[];
}

/** Grid column cell format (contract §7). */
export type AnalystColumnType =
  | 'text'
  | 'number'
  | 'currency'
  | 'percent'
  | 'days'
  | 'status';

export interface AnalystColumn {
  field: string;
  label: string;
  type?: AnalystColumnType;
}

/**
 * Chart-area config (contract §5). Only the fields relevant to a given
 * chartType / data shape are read; the rest are ignored.
 */
export interface AnalystChartConfig {
  chartType: AnalystChartType;
  title?: string;
  xLabel?: string;
  yLabel?: string;
  colors?: string[];

  /** Tabular rows. For CATEGORICAL/HIERARCHY/XY the shape differs per family. */
  data?: Record<string, unknown>[];

  // SERIES (cartesian)
  xField?: string;
  /** Alias for `xField` (legacy compat). */
  categoryField?: string;
  /** Single-series shorthand when `yFields` is omitted (legacy compat). */
  yField?: string;
  yFields?: AnalystSeries[];
  seriesField?: string;
  /** bullet — default target when a row has no per-row target. */
  target?: number;
  /** bullet — column name containing the per-row target value. */
  targetField?: string;

  // CATEGORICAL — gauge scale
  min?: number;
  max?: number;
  /** Reserved for future gauge threshold bands. */
  thresholds?: number[];
  /** Gauge-only shorthand when `data[]` has one implicit reading. */
  value?: number;

  // XY (heatmap / scatter)
  xCategories?: (string | number)[];
  yCategories?: (string | number)[];
  /** heatmap / scatter — column name holding the cell / point value. */
  valueField?: string;
}

/** A single metric tile (contract §6). */
export interface AnalystMetricTile {
  label: string;
  value: number | string;
  format?: AnalystValueFormat;
  delta?: AnalystDelta;
  sparkline?: AnalystSparkline;
}

/** Metric block config (contract §6). Layout inferred from `tiles.length`. */
export interface AnalystMetricConfig {
  title?: string;
  tiles?: AnalystMetricTile[];
}

/** Drill-through sub-table on a grid row (contract §7 — `rows[].detail`). */
export interface AnalystGridDetail {
  columns: AnalystColumn[];
  rows: Record<string, unknown>[];
}

export interface AnalystGridRow extends Record<string, unknown> {
  detail?: AnalystGridDetail;
}

/** Grid block config (contract §7). */
export interface AnalystGridConfig {
  title?: string;
  columns: AnalystColumn[];
  rows: AnalystGridRow[];
  pageSize?: number;
}

/** Universal payload (contract §2 — payload field responsibilities). */
export interface AnalystPayload {
  query?: string;
  title?: string;
  narrative?: string;
  markdown?: string;
  sql?: string;
  drilldowns?: string[];
  /** Primary data array — chart rows, metric tiles, or grid rows. Sits at payload level for consistency. */
  data?: Record<string, unknown>[] | AnalystMetricTile[];
  config?: AnalystChartConfig | AnalystMetricConfig | AnalystGridConfig;
}

export interface AnalystBlock {
  type?: AnalystBlockType;
  payload?: AnalystPayload;
}

export interface AnalystResponse extends AnalystBlock {
  block?: AnalystBlock;
  [key: string]: unknown;
}

/** Named chart colour tokens (Analyst Design Standards §08). */
export const CHART_TOKENS: Record<string, string> = {
  'chart-primary': '#00AEEF',
  'chart-accent': '#003A79',
  'chart-highlight': '#D4007F',
  'chart-grid': '#E8EDF2',
  'chart-bg': '#FFFFFF',
  'chart-amber': '#F5A623',
  // multi-series palette: cyan -> magenta -> navy -> tints
  'chart-1': '#00AEEF',
  'chart-2': '#D4007F',
  'chart-3': '#003A79',
  'chart-4': '#7FC8EC',
  'chart-5': '#E58BBE',
  'chart-6': '#6B8FB5',
};

/** Resolve a token name to a hex string. Falls back to chart-accent (#003A79). */
export function resolveColor(token?: string, fallback = CHART_TOKENS['chart-accent']): string {
  if (!token) return fallback;
  return CHART_TOKENS[token] ?? fallback;
}

export function resolvePalette(colors?: string[]): string[] {
  if (Array.isArray(colors) && colors.length > 0) {
    return colors.map((color) => resolveColor(color));
  }
  return [CHART_TOKENS['chart-accent']];
}

/**
 * Coerce an unknown value to a primitive string. Avoids the
 * `[object Object]` stringification that `String(unknown)` can produce.
 */
export function asString(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
}

/** Format a numeric value according to a named format. */
export function formatValue(value: number | string | undefined, format?: AnalystValueFormat): string {
  if (value === undefined || value === null || value === '') return '';
  const numberValue = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numberValue)) return String(value);

  switch (format) {
    case 'percent':
      return `${numberValue}%`;
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: numberValue % 1 === 0 ? 0 : 2,
      }).format(numberValue);
    case 'days':
      return `${numberValue}d`;
    case 'number-compact':
      return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(numberValue);
    case 'number':
    default:
      return new Intl.NumberFormat('en-US').format(numberValue);
  }
}

/** Read the active block from either the wrapped or top-level shape (contract §2). */
export function normalizeResponse(response: AnalystResponse | undefined): {
  type?: AnalystBlockType;
  payload: AnalystPayload;
  drilldowns: string[];
} {
  const block: AnalystBlock = response?.block ?? response ?? {};
  const payload: AnalystPayload = block.payload ?? {};
  const drilldowns = Array.isArray(payload.drilldowns) ? payload.drilldowns : [];
  return {
    type: block.type,
    payload,
    drilldowns,
  };
}

/**
 * Empty-state detection (contract §3). The API never sends `state`; the
 * frontend derives `empty` from the presence of renderable body data.
 * Data lives at `payload.data` for chart, metric, and grid types.
 */
export function isEmptyPayload(type: AnalystBlockType | undefined, payload: AnalystPayload): boolean {
  switch (type) {
    case 'chart':
    case 'metric':
    case 'grid':
      return !Array.isArray(payload.data) || payload.data.length === 0;
    case 'markdown':
      return !payload.markdown || payload.markdown.trim() === '';
    default:
      return false;
  }
}
